import axios from 'axios';
import { formatReceipt } from '../../LoyaltyPage/helpers';
import { CurrentReceiptData } from '../../LoyaltyPage/types';
import { CouponQuote, couponReceiptError, money } from './model';
import { couponOrderNumber } from './order';

export interface CouponProgress {
  active: boolean;
  orderNumber: string;
  syncError: boolean;
}

export class CouponError extends Error {
  constructor(public key: string, public uncertain = false) {
    super(key);
  }
}
const errorCodes: Record<string, string> = {
  '400143': 'BONUS_CODE_INVALID',
  '400201': 'INVALID_CODE',
  '400202': 'SELECT_RECEIPT',
  '404201': 'NO_RECEIPT',
  '400162': 'NotExist',
  '400163': 'ExceededMaxUsages',
  '400164': 'WrongIntegration',
  '400165': 'ProductNotInCheck',
  '400166': 'InvalidQuantity',
  '400167': 'CheckAlreadyHasCoupon',
};
export const couponErrorKey = (error: any) => {
  if (error instanceof CouponError) return error.key;
  const code = String(error?.response?.data?.code ?? '').replace(
    /^[A-Za-z]+(?=\d{6}$)/,
    ''
  );
  if (errorCodes[code]) return errorCodes[code];
  if (error?.response?.status === 401 || error?.response?.status === 403)
    return 'ACCESS_ERROR';
  return error?.response ? 'SERVICE_ERROR' : 'CONNECTION_ERROR';
};
const base = `${process.env.REACT_APP_API_BASE_URL}`;
export const CouponService = {
  async check(
    code: string,
    docId?: string,
    signal?: AbortSignal
  ): Promise<CouponQuote> {
    const response = await axios.get(`${base}/api/v2/loyalty/coupon/check`, {
      params: { code, ...(docId ? { doc_id: docId } : {}) },
      signal,
      timeout: 10000,
    });
    if (response.data?.status === 'error')
      throw new CouponError(
        response.data?.code ? couponErrorKey({ response }) : 'INVALID_RESPONSE'
      );
    return response.data;
  },
  async receipt(docId: string): Promise<CurrentReceiptData> {
    const response = await axios.get(`${base}/api/v1/orders/soft/products`, {
      timeout: 10000,
    });
    if (
      response.data?.status === 'error' ||
      !Array.isArray(response.data?.checks)
    )
      throw new CouponError('INVALID_RESPONSE');
    const receipt = formatReceipt(response.data)?.checks?.find(
      (item: CurrentReceiptData) => String(item.doc_id) === String(docId)
    );
    if (!receipt) throw new CouponError('NO_RECEIPT');
    return receipt;
  },
  async progress(docId: string): Promise<CouponProgress> {
    // The gateway may temporarily omit an active receipt, or retain an old
    // one. Only a valid read of the local cashier can establish its presence.
    // Remote data is still useful for an actual order number, never for payment.
    const [cashier, remote] = await Promise.all([
      axios.get('http://127.0.0.1:8000/check', { timeout: 10000 }),
      CouponService.receipt(docId).then(
        (receipt) => ({ receipt, failed: false }),
        () => ({ receipt: null, failed: true })
      ),
    ]);
    const { data } = cashier;
    if (
      data?.status !== 'success' ||
      !Array.isArray(data.data) ||
      !data.data.every(
        (receipt: any) =>
          receipt &&
          (typeof receipt.doc_id === 'string' ||
            (typeof receipt.doc_id === 'number' &&
              Number.isFinite(receipt.doc_id))) &&
          String(receipt.doc_id).trim().length > 0
      )
    )
      throw new CouponError('INVALID_RESPONSE');
    const current = data.data.find(
      (receipt: any) => String(receipt.doc_id) === String(docId)
    );
    return {
      active: !!current,
      orderNumber:
        couponOrderNumber(remote.receipt) || couponOrderNumber(current),
      syncError: remote.failed,
    };
  },
  async scan(
    docId: string,
    items: { ware_id: string; price: number; quantity: number }[],
    orderNumber?: string
  ) {
    const response = await axios.post(
      'http://127.0.0.1:8000/scan',
      {
        payment_type: 'in_place',
        // Match the existing PL format before an actual order number is known.
        // Standart's positional parser requires this field in this sequence.
        order_number: orderNumber || 'PA',
        doc_id: docId,
        items,
      },
      { timeout: 10000 }
    );
    if (response.data?.status !== 'success')
      throw new CouponError('SCAN_ERROR');
  },
  async verifyPrice(docId: string, wareId: string, price: number) {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      // Poll sequentially so a late/stale response cannot override a newer receipt.
      // eslint-disable-next-line no-await-in-loop
      const receipt = await CouponService.receipt(docId);
      if (couponReceiptError(receipt)) throw new CouponError('RECEIPT_CHANGED');
      if (
        receipt.items[0].ware_id === wareId &&
        money(receipt.items[0].base_price) === money(price) &&
        money(receipt.items[0].total_price) === money(price)
      )
        return receipt;
      if (attempt < 2) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });
      }
    }
    throw new CouponError('PRICE_NOT_APPLIED');
  },
  async confirm(payload: object) {
    const { isPartner, ...body } = payload as object & {
      isPartner?: boolean;
    };
    // Coupon checkout must use the same order-creating path as the existing
    // loyalty flow. A single request both confirms the discounts and returns
    // the real PA order number; never call both endpoints as a fallback.
    try {
      const response = await axios.post(
        `${base}/api/v1/orders/confirm${isPartner ? '/partner' : ''}`,
        body,
        { timeout: 15000 }
      );
      if (response.data?.status !== 'success') {
        if (response.data?.status !== 'error')
          throw new CouponError('CONFIRM_UNKNOWN', true);
        throw new CouponError(
          response.data?.code ? couponErrorKey({ response }) : 'CONFIRM_ERROR'
        );
      }
      return response.data;
    } catch (error: any) {
      if (error instanceof CouponError) throw error;
      const definitiveRejection =
        error.response?.status >= 400 && error.response?.status < 500;
      throw new CouponError(
        definitiveRejection ? couponErrorKey(error) : 'CONFIRM_UNKNOWN',
        !definitiveRejection
      );
    }
  },
  async setCashierPaymentMethod(orderNumber: string) {
    try {
      const response = await axios.put(
        `${base}/api/v1/orders/${orderNumber}/payments/change/partner`,
        { order_id: orderNumber },
        { timeout: 15000 }
      );
      if (response.data?.status === 'error')
        throw new CouponError('PAYMENT_METHOD_ERROR');
      return response.data;
    } catch (error) {
      if (error instanceof CouponError) throw error;
      throw new CouponError('PAYMENT_METHOD_ERROR', true);
    }
  },
};
